/**
 * Native Bridge - Detects Capacitor environment and provides native iOS/Android features.
 * Falls back to web APIs when running in browser.
 * 
 * Apple App Store compliance: This bridges native capabilities (haptics, share, biometrics)
 * that differentiate the app from a simple WebView wrapper.
 */

// Detect if running inside Capacitor native shell
export const isNative = () => {
  return typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.() === true;
};

export const isIOS = () => {
  return isNative() && window.Capacitor?.getPlatform?.() === 'ios';
};

export const isAndroid = () => {
  return isNative() && window.Capacitor?.getPlatform?.() === 'android';
};

// ============================================
// SPLASH SCREEN — Hide native splash when app is ready
// ============================================
export const hideNativeSplash = async () => {
  if (!isNative()) return;
  try {
    const mod = await import('@capacitor/splash-screen');
    if (mod?.SplashScreen) {
      await mod.SplashScreen.hide();
    }
  } catch (e) {
    console.warn('SplashScreen hide failed:', e);
  }
};

// ============================================
// HAPTICS — Native tactile feedback
// ============================================
let HapticsPlugin = null;

const loadHaptics = async () => {
  if (HapticsPlugin) return HapticsPlugin;
  try {
    const mod = await import('@capacitor/haptics');
    HapticsPlugin = mod.Haptics;
    return HapticsPlugin;
  } catch {
    return null;
  }
};

// Pre-load haptics immediately when native (don't wait for first tap)
if (isNative()) {
  loadHaptics();
}

export const haptic = {
  /** Light tap — button presses, toggles */
  light: async () => {
    if (isNative()) {
      const h = await loadHaptics();
      if (h) return h.impact({ style: 'LIGHT' });
    }
    if ('vibrate' in navigator) navigator.vibrate(10);
  },

  /** Medium tap — selections, confirmations */
  medium: async () => {
    if (isNative()) {
      const h = await loadHaptics();
      if (h) return h.impact({ style: 'MEDIUM' });
    }
    if ('vibrate' in navigator) navigator.vibrate(20);
  },

  /** Heavy tap — purchases, pack rips, reveals */
  heavy: async () => {
    if (isNative()) {
      const h = await loadHaptics();
      if (h) return h.impact({ style: 'HEAVY' });
    }
    if ('vibrate' in navigator) navigator.vibrate([30, 10, 30]);
  },

  /** Success — completed purchase, card revealed */
  success: async () => {
    if (isNative()) {
      const h = await loadHaptics();
      if (h) return h.notification({ type: 'SUCCESS' });
    }
    if ('vibrate' in navigator) navigator.vibrate([10, 50, 20]);
  },

  /** Warning — low balance, approaching limit */
  warning: async () => {
    if (isNative()) {
      const h = await loadHaptics();
      if (h) return h.notification({ type: 'WARNING' });
    }
    if ('vibrate' in navigator) navigator.vibrate([20, 30, 20]);
  },

  /** Error — failed action, ban */
  error: async () => {
    if (isNative()) {
      const h = await loadHaptics();
      if (h) return h.notification({ type: 'ERROR' });
    }
    if ('vibrate' in navigator) navigator.vibrate([50, 30, 50, 30, 50]);
  },

  /** Selection changed — scrolling through cards, rarity reveals */
  selection: async () => {
    if (isNative()) {
      const h = await loadHaptics();
      if (h) return h.selectionChanged();
    }
  },
};

// ============================================
// SHARE — Native share sheet
// ============================================
export const nativeShare = async ({ title, text, url, dialogTitle }) => {
  if (isNative()) {
    try {
      const { Share } = await import('@capacitor/share');
      await Share.share({ title, text, url, dialogTitle: dialogTitle || 'Share via' });
      return true;
    } catch {
      // fallback below
    }
  }
  // Web fallback
  if (navigator.share) {
    try {
      await navigator.share({ title, text, url });
      return true;
    } catch {
      return false;
    }
  }
  // Final fallback: copy to clipboard
  try {
    await navigator.clipboard.writeText(url || text || title);
    return 'copied';
  } catch {
    return false;
  }
};

// ============================================
// STATUS BAR — iOS status bar styling
// ============================================
export const configureStatusBar = async () => {
  if (!isNative()) return;
  try {
    const { StatusBar } = await import('@capacitor/status-bar');
    await StatusBar.setStyle({ style: 'DARK' });
    await StatusBar.setBackgroundColor({ color: '#000000' });
  } catch {
    // Not available
  }
};

// ============================================
// APP LIFECYCLE — deep links, back button
// ============================================
export const setupAppLifecycle = (onDeepLink) => {
  if (!isNative()) return () => {};
  let cleanup = () => {};
  import('@capacitor/app').then(({ App }) => {
    const urlListener = App.addListener('appUrlOpen', (event) => {
      if (onDeepLink) onDeepLink(event.url);
    });
    cleanup = () => urlListener.remove();
  }).catch(() => {});
  return () => cleanup();
};

// ============================================
// BIOMETRIC AUTH — Face ID / Touch ID gate
// ============================================

/**
 * Check if biometric auth (Face ID / Touch ID) is available.
 * Returns { available: boolean, biometryType: 'faceId' | 'touchId' | 'none' }
 */
export const checkBiometricAvailability = async () => {
  if (!isNative()) return { available: false, biometryType: 'none' };
  try {
    const { NativeBiometric } = await import('@capgo/capacitor-native-biometric');
    const result = await NativeBiometric.isAvailable();
    return {
      available: result.isAvailable,
      biometryType: result.biometryType === 1 ? 'touchId' : result.biometryType === 2 ? 'faceId' : 'biometric',
    };
  } catch {
    return { available: false, biometryType: 'none' };
  }
};

/**
 * Prompt user for biometric verification (Face ID / Touch ID).
 * Returns true if authenticated, false otherwise.
 */
export const authenticateWithBiometrics = async (reason = 'Verify your identity') => {
  if (!isNative()) return true; // Skip in browser
  try {
    const { NativeBiometric } = await import('@capgo/capacitor-native-biometric');
    const avail = await NativeBiometric.isAvailable();
    if (!avail.isAvailable) return true; // No biometrics = skip
    await NativeBiometric.verifyIdentity({
      reason,
      title: 'BLK PACKS',
      subtitle: reason,
      description: 'Use Face ID or Touch ID to continue',
    });
    return true;
  } catch {
    return false; // User cancelled or failed
  }
};

/**
 * Save credentials securely using biometric-protected keychain.
 */
export const saveCredentials = async (email, token) => {
  if (!isNative()) return;
  try {
    const { NativeBiometric } = await import('@capgo/capacitor-native-biometric');
    await NativeBiometric.setCredentials({
      username: email,
      password: token,
      server: 'blkpacks.fun',
    });
  } catch {
    // Silent fail — not critical
  }
};

/**
 * Retrieve saved credentials from biometric-protected keychain.
 * Returns { email, token } or null.
 */
export const getCredentials = async () => {
  if (!isNative()) return null;
  try {
    const { NativeBiometric } = await import('@capgo/capacitor-native-biometric');
    const avail = await NativeBiometric.isAvailable();
    if (!avail.isAvailable) return null;
    await NativeBiometric.verifyIdentity({
      reason: 'Sign in to BLK PACKS',
      title: 'BLK PACKS',
      subtitle: 'Sign in with Face ID',
    });
    const creds = await NativeBiometric.getCredentials({ server: 'blkpacks.fun' });
    return { email: creds.username, token: creds.password };
  } catch {
    return null;
  }
};


// ============================================
// LOCAL PUSH NOTIFICATIONS — Drop reminders
// ============================================

let LocalNotificationsPlugin = null;

const loadLocalNotifications = async () => {
  if (LocalNotificationsPlugin) return LocalNotificationsPlugin;
  try {
    const mod = await import('@capacitor/local-notifications');
    LocalNotificationsPlugin = mod.LocalNotifications;
    // Request permission
    const perm = await LocalNotificationsPlugin.checkPermissions();
    if (perm.display !== 'granted') {
      await LocalNotificationsPlugin.requestPermissions();
    }
    return LocalNotificationsPlugin;
  } catch {
    return null;
  }
};

/**
 * Schedule a local notification for a drop reminder.
 * @param {string} dropId - Unique drop ID
 * @param {string} dropName - Name of the drop
 * @param {Date} dropDate - When the drop goes live
 * @param {number} minutesBefore - Remind X minutes before (default: 60)
 */
export const scheduleDropReminder = async (dropId, dropName, dropDate, minutesBefore = 60) => {
  if (!isNative()) return false;
  const ln = await loadLocalNotifications();
  if (!ln) return false;

  const remindAt = new Date(dropDate.getTime() - minutesBefore * 60 * 1000);
  if (remindAt <= new Date()) return false; // Already past

  // Use a numeric hash of the dropId as notification ID
  const notifId = Math.abs(dropId.split('').reduce((a, c) => ((a << 5) - a) + c.charCodeAt(0), 0) % 100000);

  try {
    await ln.schedule({
      notifications: [
        {
          id: notifId,
          title: 'Drop Alert!',
          body: `${dropName} goes live in ${minutesBefore} minutes! Get ready to rip.`,
          schedule: { at: remindAt },
          sound: 'default',
          smallIcon: 'ic_notification',
          largeIcon: 'ic_notification',
          extra: { dropId },
        },
      ],
    });
    return true;
  } catch {
    return false;
  }
};

/**
 * Cancel a scheduled drop reminder.
 */
export const cancelDropReminder = async (dropId) => {
  if (!isNative()) return;
  const ln = await loadLocalNotifications();
  if (!ln) return;
  const notifId = Math.abs(dropId.split('').reduce((a, c) => ((a << 5) - a) + c.charCodeAt(0), 0) % 100000);
  try {
    await ln.cancel({ notifications: [{ id: notifId }] });
  } catch { /* silent */ }
};

/**
 * Schedule a "drop is live" notification for immediate display.
 */
export const notifyDropLive = async (dropName) => {
  if (!isNative()) return;
  const ln = await loadLocalNotifications();
  if (!ln) return;
  try {
    await ln.schedule({
      notifications: [{
        id: Date.now() % 100000,
        title: 'Drop is LIVE!',
        body: `${dropName} is available now. Open the app to rip your pack!`,
        schedule: { at: new Date(Date.now() + 1000) },
        sound: 'default',
      }],
    });
  } catch { /* silent */ }
};


// ============================================
// APP ICON BADGE — Live Break "right now" counter
// ============================================
// Shows a red number on the BLK PACKS app icon mirroring the
// "LIVE NOW" counter on the Apple Wallet Gamer Pass. Visible even
// when the device is locked + Wallet notifications are silenced —
// it's the "is something happening?" signal of last resort.
//
// iOS requires UNAuthorizationOptionBadge to be granted before the
// badge surfaces. We request it lazily on first set.

let _badgePlugin = null;
let _badgePermissionRequested = false;

const loadBadge = async () => {
  if (_badgePlugin) return _badgePlugin;
  if (!isNative()) return null;
  try {
    const mod = await import('@capawesome/capacitor-badge');
    _badgePlugin = mod.Badge;
    return _badgePlugin;
  } catch {
    return null;
  }
};

const ensureBadgePermission = async (Badge) => {
  if (_badgePermissionRequested) return true;
  _badgePermissionRequested = true;
  try {
    const perm = await Badge.checkPermissions();
    if (perm.display === 'granted') return true;
    const req = await Badge.requestPermissions();
    return req.display === 'granted';
  } catch {
    return false;
  }
};

/**
 * Set the app icon badge to a count. Pass 0 (or null) to clear.
 * Silently no-ops in browser or if iOS denied badge permission.
 */
export const setAppBadge = async (count) => {
  const Badge = await loadBadge();
  if (!Badge) return;
  try {
    const granted = await ensureBadgePermission(Badge);
    if (!granted) return;
    const n = Math.max(0, parseInt(count, 10) || 0);
    if (n === 0) {
      await Badge.clear();
    } else {
      await Badge.set({ count: n });
    }
  } catch {
    /* silent — badge isn't worth surfacing failures */
  }
};

export const clearAppBadge = () => setAppBadge(0);
